import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    collection,
    onSnapshot,
    query,
    where,
    orderBy,
    doc,
    getDoc,
    getDocs,
    documentId
} from 'firebase/firestore';
import { FileText, Loader2, BookOpen, ChevronLeft, Calendar, Info, Users, Filter, ChevronDown, Check, Download } from 'lucide-react';

// --- IMPORTS DES LIBRAIRIES PDF ---
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Constante pour la limite des requêtes 'in' de Firestore
const FIRESTORE_IN_QUERY_LIMIT = 10;

/**
 * Fonction pour récupérer le nom de l'école.
 * Déplacée ici pour être utilisée par MyBulletins et BulletinDetail.
 */
const getSchoolInfo = async (db, schoolId) => {
    if (!db || !schoolId) return { name: "École Inconnue", telephone: "N/A" };
    try {
        const schoolDocRef = doc(db, 'schools', schoolId);
        const schoolDocSnap = await getDoc(schoolDocRef);
        if (schoolDocSnap.exists()) {
            const data = schoolDocSnap.data();
            return {
                name: data.name || "Nom de l'école non spécifié",
                telephone: data.telephone_ecole || null,
            };
        }
    } catch (error) {
        console.error("Erreur lors de la récupération des infos de l'école:", error);
    }
    return { name: "École Inconnue", telephone: null };
};


/**
 * Composant permettant à un élève (ou parent) de consulter ses propres bulletins de notes.
 * @param {object} props - Les propriétés du composant.
 * @param {object} props.db - Instance de Firebase Firestore.
 * @param {function} props.setError - Fonction pour afficher une erreur.
 * @param {string} props.schoolId - ID de l'école sélectionnée.
 * @param {string} props.currentUserId - L'UID d'authentification Firebase de l'utilisateur (élève ou parent).
 * @param {string} props.userRole - Le rôle de l'utilisateur ('student', 'parent', etc.).
 */
const MyBulletins = ({ db, setError, schoolId, currentUserId, userRole }) => {

    // --- États Partagés ---
    const [bulletins, setBulletins] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isResolvingIdentity, setIsResolvingIdentity] = useState(true);
    const [selectedBulletin, setSelectedBulletin] = useState(null);
    const [schoolInfo, setSchoolInfo] = useState({ name: "École", telephone: null }); // Nouvel état pour les infos de l'école

    // --- États Spécifiques Parent ---
    const [children, setChildren] = useState([]);
    const [selectedChildId, setSelectedChildId] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    // --- États Spécifiques Élève ---
    const [studentEleveId, setStudentEleveId] = useState('');
    const [studentClassData, setStudentClassData] = useState({ name: 'Chargement...' });

    const [eleveIdToFetch, setEleveIdToFetch] = useState('');

    const isParentRole = userRole === 'parent' || userRole === 'superAdmin';

    // --- États pour les filtres ---
    const [filterYear, setFilterYear] = useState('all');
    const [filterPeriod, setFilterPeriod] = useState('all');
    const [availableYears, setAvailableYears] = useState([]);
    const [availablePeriods, setAvailablePeriods] = useState([]);


    // --- Configuration de l'Année Scolaire Actuelle ---
    const getCurrentSchoolYear = () => {
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth();
        return currentMonth >= 8 ? `${currentYear}-${currentYear + 1}` : `${currentYear - 1}-${currentYear}`;
    };
    const currentSchoolYear = useMemo(getCurrentSchoolYear, []);


    // --- Étape initiale: Récupérer les infos de l'école ---
    useEffect(() => {
        if (!db || !schoolId) return;
        const fetchInfo = async () => {
            const info = await getSchoolInfo(db, schoolId);
            setSchoolInfo(info);
        };
        fetchInfo();
    }, [db, schoolId]);


    // ----------------------------------------------------------------------------------
    // --- LOGIQUE PARENT: Résolution des enfants liés (Optimisé) ---
    // ----------------------------------------------------------------------------------
    useEffect(() => {
        if (!currentUserId || !schoolId || !db || !isParentRole) {
            if (isParentRole) setIsResolvingIdentity(false);
            setChildren([]);
            return;
        }

        const fetchChildrenData = async () => {
            setIsResolvingIdentity(true);
            try {
                const userDocRef = doc(db, 'users', currentUserId);
                const userDocSnap = await getDoc(userDocRef);

                const linkedEleveIds = userDocSnap.exists() && userDocSnap.data().childrenEleveIds
                    ? userDocSnap.data().childrenEleveIds.filter(id => id && typeof id === 'string')
                    : [];

                if (linkedEleveIds.length === 0) {
                    setChildren([]);
                    setEleveIdToFetch('');
                    setIsResolvingIdentity(false);
                    return;
                }

                const eleveChunks = [];
                for (let i = 0; i < linkedEleveIds.length; i += FIRESTORE_IN_QUERY_LIMIT) {
                    eleveChunks.push(linkedEleveIds.slice(i, i + FIRESTORE_IN_QUERY_LIMIT));
                }

                let eleveDetailsMap = {};
                let uniqueClassIds = new Set();
                let enrollmentsByStudentId = {};

                for (const chunk of eleveChunks) {
                    const elevesCollectionRef = collection(db, 'schools', schoolId, 'eleves');
                    const elevesSnap = await getDocs(query(elevesCollectionRef, where(documentId(), 'in', chunk)));
                    elevesSnap.docs.forEach(eleveDoc => {
                        const eleveData = eleveDoc.data();
                        eleveDetailsMap[eleveDoc.id] = {
                            id: eleveDoc.id,
                            name: eleveData.name || `${eleveData.firstName || ''} ${eleveData.lastName || ''}`.trim() || eleveDoc.id,
                            authId: eleveData.authId || null
                        };
                    });

                    const enrollmentCollectionRef = collection(db, 'schools', schoolId, 'enrollments');
                    const enrollmentSnap = await getDocs(query(
                        enrollmentCollectionRef,
                        where('studentId', 'in', chunk),
                        where('schoolYear', '==', currentSchoolYear)
                    ));
                    enrollmentSnap.docs.forEach(enrollmentDoc => {
                        const enrollmentData = enrollmentDoc.data();
                        enrollmentsByStudentId[enrollmentData.studentId] = enrollmentData.classId;
                        if (enrollmentData.classId) {
                            uniqueClassIds.add(enrollmentData.classId);
                        }
                    });
                }

                let classNamesMap = {};
                if (uniqueClassIds.size > 0) {
                    const classIdChunks = [];
                    const uniqueClassIdsArray = Array.from(uniqueClassIds);
                    for (let i = 0; i < uniqueClassIdsArray.length; i += FIRESTORE_IN_QUERY_LIMIT) {
                        classIdChunks.push(uniqueClassIdsArray.slice(i, i + FIRESTORE_IN_QUERY_LIMIT));
                    }

                    for (const classChunk of classIdChunks) {
                        const classesCollectionRef = collection(db, 'schools', schoolId, 'classes');
                        const classesSnap = await getDocs(query(classesCollectionRef, where(documentId(), 'in', classChunk)));
                        classesSnap.docs.forEach(classDoc => {
                            classNamesMap[classDoc.id] = classDoc.data().nom || 'Inconnue';
                        });
                    }
                }

                const allChildren = linkedEleveIds.map(eleveId => {
                    const eleve = eleveDetailsMap[eleveId] || { id: eleveId, name: eleveId, authId: null };
                    const classId = enrollmentsByStudentId[eleveId];
                    const className = classId ? classNamesMap[classId] || 'Inconnue' : 'Inconnue';
                    return { ...eleve, className };
                });

                setChildren(allChildren);
                if (allChildren.length > 0) {
                    const initialSelectedId = allChildren[0].id;
                    setSelectedChildId(initialSelectedId);
                    setEleveIdToFetch(initialSelectedId);
                } else {
                    setEleveIdToFetch('');
                }

            } catch (err) {
                console.error("Erreur lors de la récupération des enfants liés (Parent View):", err);
                setError("Erreur lors du chargement des données de vos enfants.");
                setChildren([]);
                setEleveIdToFetch('');
            } finally {
                setIsResolvingIdentity(false);
            }
        };

        fetchChildrenData();
    }, [db, currentUserId, schoolId, isParentRole, setError, currentSchoolYear]);


    // ----------------------------------------------------------------------------------
    // --- LOGIQUE ÉLÈVE: Résoudre l'eleveId (Auth UID) ---
    // ----------------------------------------------------------------------------------
    useEffect(() => {
        if (isParentRole || !currentUserId || !schoolId || !db) {
            if (!isParentRole) setIsResolvingIdentity(false);
            setStudentEleveId('');
            setStudentClassData({ name: 'Chargement...' });
            return;
        }

        setStudentEleveId(currentUserId);
        setEleveIdToFetch(currentUserId);

        const resolveStudentClass = async () => {
            try {
                const enrollmentCollectionRef = collection(db, 'schools', schoolId, 'enrollments');
                const activeEnrollmentQuery = query(
                    enrollmentCollectionRef,
                    where('studentAuthUID', '==', currentUserId),
                    where('schoolYear', '==', currentSchoolYear)
                );
                const enrollmentSnapshot = await getDocs(activeEnrollmentQuery);

                if (!enrollmentSnapshot.empty) {
                    const classeId = enrollmentSnapshot.docs[0].data().classId;
                    if (classeId) {
                        const classDocRef = doc(db, `schools/${schoolId}/classes`, classeId);
                        const classDocSnap = await getDoc(classDocRef);
                        setStudentClassData({ name: classDocSnap.exists() ? classDocSnap.data().nom : 'Classe Inconnue' });
                    }
                }
            } catch (e) {
                console.warn("Could not resolve student class data:", e);
                setStudentClassData({ name: 'Erreur Classe' });
            } finally {
                setIsResolvingIdentity(false);
            }
        };

        resolveStudentClass();
    }, [db, currentUserId, schoolId, isParentRole, currentSchoolYear]);


    // ----------------------------------------------------------------------------------
    // --- Écouteur de Données Principal (Bulletins) ---
    // ----------------------------------------------------------------------------------
    useEffect(() => {
        if (isResolvingIdentity) {
            setIsLoading(true);
            setBulletins([]);
            return;
        }

        if (!eleveIdToFetch) {
            setIsLoading(false);
            setBulletins([]);
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const bulletinsRef = collection(db, `schools/${schoolId}/bulletins`);
            // Détermine le champ de requête approprié selon le rôle
            // Pour le parent, eleveIdToFetch est l'ID du document élève, on le compare à eleveId (du bulletin)
            // Pour l'élève, eleveIdToFetch est l'Auth UID, on le compare à eleveDetails.authId (du bulletin)
            // C'est ici qu'il faut changer :
            let fieldToQuery = isParentRole ? 'eleveId' : 'eleveDetails.authId';
            let queryConstraints = [
                where(fieldToQuery, '==', eleveIdToFetch),
                orderBy('anneeScolaire', 'desc'),
                orderBy('periode', 'desc')
            ];

            // --- FILTRES ---
            if (filterYear !== 'all') {
                queryConstraints.push(where('anneeScolaire', '==', filterYear));
            }
            if (filterPeriod !== 'all') {
                queryConstraints.push(where('periode', '==', filterPeriod));
            }
            // Assurez-vous que les clauses orderBy correspondent aux clauses where (Firestore Indexing)
            // Si vous avez un where sur anneeScolaire, l'orderBy 'anneeScolaire' doit venir en premier.
            // Si vous avez un where sur periode, l'orderBy 'periode' doit venir après anneeScolaire.


            const q = query(bulletinsRef, ...queryConstraints);

            const unsubscribe = onSnapshot(q, (snapshot) => {
                const fetchedBulletins = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                setBulletins(fetchedBulletins);

                // Mettre à jour les options de filtre disponibles après le chargement initial
                const years = new Set(fetchedBulletins.map(b => b.anneeScolaire).filter(Boolean));
                const periods = new Set(fetchedBulletins.map(b => b.periode).filter(Boolean));
                setAvailableYears(Array.from(years).sort().reverse());
                setAvailablePeriods(Array.from(periods).sort()); // Ou un ordre spécifique si vous avez (Trimestre 1, Trimestre 2,...)
                
                setIsLoading(false);
            }, (error) => {
                console.error("Erreur de chargement des bulletins:", error);
                setError("Erreur lors du chargement de vos bulletins.");
                setIsLoading(false);
            });

            return () => unsubscribe();
        } catch (e) {
            console.error("Erreur de setup Firestore pour les bulletins (catch général):", e);
            setError("Erreur de configuration de la base de données.");
            setIsLoading(false);
        }
    }, [db, schoolId, eleveIdToFetch, isParentRole, isResolvingIdentity, setError, filterYear, filterPeriod]); // Ajouter les filtres aux dépendances


    // Gérer le changement d'enfant sélectionné
    const handleChildSelection = useCallback((childId) => {
        setSelectedChildId(childId);
        setEleveIdToFetch(childId);
        setIsDropdownOpen(false);
        setSelectedBulletin(null);
        setFilterYear('all'); // Réinitialiser les filtres lors du changement d'enfant
        setFilterPeriod('all');
    }, []);

    // Détermination de l'entité affichée
    const entityToDisplay = useMemo(() => {
        if (isParentRole) {
            const child = children.find(c => c.id === selectedChildId) || children[0];
            return child || null;
        }
        return studentEleveId ? {
            id: studentEleveId,
            name: `Votre Compte`,
            className: studentClassData.name
        } : null;
    }, [isParentRole, children, selectedChildId, studentEleveId, studentClassData.name]);


    // --- Composant d'affichage détaillé du bulletin ---
    const handleViewBulletin = (bulletin) => {
        setSelectedBulletin(bulletin);
    };

    const handleBackToList = () => {
        setSelectedBulletin(null);
    };

    // Fonction d'exportation PDF pour un bulletin spécifique
    const exportBulletinToPDF = useCallback((bulletin) => {
        const input = document.getElementById('bulletin-detail-content');

        if (!input) {
            alert("Erreur: Le contenu du bulletin n'a pas été trouvé pour l'exportation.");
            return;
        }

        html2canvas(input, {
            scale: 2,
            useCORS: true,
            allowTaint: true
        }).then((canvas) => {
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4'); // 'p' for portrait, 'mm' for millimeters, 'a4' for A4 size
            const imgWidth = 200; // Largeur de l'image dans le PDF
            const pageHeight = pdf.internal.pageSize.getHeight();
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            let heightLeft = imgHeight;
            let position = 10; // Marge supérieure initiale

            // En-tête du document PDF
            pdf.setFontSize(16);
            pdf.text(schoolInfo.name || "École", 10, position);
            position += 8;
            pdf.setFontSize(12);
            pdf.text(`Bulletin de Notes - ${bulletin.periode}`, 10, position);
            position += 6;
            pdf.text(`Année Scolaire: ${bulletin.anneeScolaire}`, 10, position);
            position += 6;
            pdf.text(`Élève: ${bulletin.eleveDetails?.name || 'N/A'} (Classe: ${bulletin.classeNom || 'N/A'})`, 10, position);
            position += 10; // Espace après l'en-tête


            pdf.addImage(imgData, 'PNG', 5, position, imgWidth, imgHeight); // Ajout de l'image du bulletin
            heightLeft -= (pageHeight - position); // Déterminer la hauteur restante de l'image par rapport au bas de la page

            // Gérer le contenu sur plusieurs pages si l'image est trop grande
            while (heightLeft > 0) {
                position = 10; // Nouvelle marge supérieure pour la page suivante
                pdf.addPage();
                // L'offset est calculé pour afficher la partie restante de l'image sur la nouvelle page
                pdf.addImage(imgData, 'PNG', 5, position - imgHeight + heightLeft, imgWidth, imgHeight);
                heightLeft -= (pageHeight - position); // Réévaluer la hauteur restante
            }

            pdf.save(`Bulletin_${bulletin.eleveDetails?.name || 'Eleve'}_${bulletin.periode}_${bulletin.anneeScolaire}.pdf`);
        }).catch(err => {
            console.error("Erreur lors de la conversion du bulletin en PDF:", err);
            alert("Échec de l'exportation PDF. Veuillez réessayer ou utiliser la fonction d'impression de votre navigateur.");
        });
    }, [schoolInfo]);


    // Le composant BulletinDetail est déplacé à l'extérieur ou comme une constante pour plus de clarté
    const BulletinDetail = ({ bulletin, schoolInfo, onBackToList, onExportToPDF }) => {
        if (!bulletin || !bulletin.detailsParMatiere) {
            return <div className="text-center text-red-500 p-8">Données de bulletin non trouvées ou incomplètes.</div>;
        }
        const totalMatieres = bulletin.detailsParMatiere.length;
        const notesArray = bulletin.detailsParMatiere.map((noteData) => ({
            matiere: noteData.matiere || 'N/A',
            note: noteData.moyenneMatiere !== undefined ? parseFloat(noteData.moyenneMatiere).toFixed(2) : 'N/A',
            moyenneClasse: bulletin.moyenneClasse !== undefined ? parseFloat(bulletin.moyenneClasse).toFixed(2) : 'N/A',
            coefficient: noteData.coefficient || 1,
            rang: noteData.rankMatiere || 'N/A',
        }));
        return (
            <div className="p-6 bg-white rounded-xl shadow-lg border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                    <button
                        onClick={onBackToList}
                        className="text-indigo-600 hover:text-indigo-800 flex items-center transition duration-150"
                    >
                        <ChevronLeft size={20} className="mr-1" /> Retour à la liste
                    </button>
                    <button
                        onClick={() => onExportToPDF(bulletin)}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-lg flex items-center transition duration-150"
                        title="Télécharger ce bulletin en PDF"
                    >
                        <Download size={18} className="mr-2" /> Télécharger PDF
                    </button>
                </div>

                {/* Contenu du bulletin à exporter, enveloppé dans un div avec ID */}
                <div id="bulletin-detail-content" className="p-4"> {/* Ajout de padding pour le contenu du PDF */}
                    {/* Les informations d'en-tête de l'école seront ajoutées par le PDF directement,
                    mais les détails spécifiques au bulletin sont ici */}
                    <div className="text-center mb-6">
                        <h1 className="text-2xl font-extrabold text-gray-900">{schoolInfo.name}</h1>
                        <h2 className="text-xl font-bold text-gray-800 mt-2">Bulletin de Notes</h2>
                        <p className="text-gray-600">Année Scolaire: {bulletin.anneeScolaire}</p>
                        <p className="text-gray-600">Période: {bulletin.periode}</p>
                        <p className="text-gray-700 font-semibold">Élève: {bulletin.eleveDetails?.name || 'N/A'} - Classe: {bulletin.classeNom || 'N/A'}</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center border p-4 rounded-lg bg-gray-50">
                        <div className=" p-2 rounded-lg">
                            <p className="text-sm text-indigo-700 font-medium">Moyenne Générale</p>
                            <p className="text-3xl font-extrabold text-indigo-900 mt-1">{bulletin.moyenneGenerale !== undefined ?
                                parseFloat(bulletin.moyenneGenerale).toFixed(2) : 'N/A'} / {bulletin.baseNotation}</p>
                        </div>
                        <div className=" p-2 rounded-lg">
                            <p className="text-sm text-green-700 font-medium">Rang</p>
                            <p className="text-3xl font-extrabold text-green-900 mt-1">{bulletin.rank || 'N/A'}</p>
                        </div>
                        <div className=" p-2 rounded-lg">
                            <p className="text-sm text-yellow-700 font-medium">Effectif</p>
                            <p className="text-3xl font-extrabold text-yellow-900 mt-1">{bulletin.effectifClasse ||
                                'N/A'}</p>
                        </div>
                    </div>

                    <h5 className="text-xl font-semibold text-gray-800 mb-3 border-b pb-2">Détail des Notes ({totalMatieres} Matières)</h5>
                    <div className="overflow-x-auto mb-6">
                        <table className="min-w-full divide-y divide-gray-200 rounded-lg overflow-hidden">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Matière</th>
                                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Note Obtenue / {bulletin.baseNotation}</th>
                                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Moyenne Classe*</th>
                                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Coeff.</th>
                                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Rang (Matière)</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {notesArray.map((noteItem, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                        <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">{noteItem.matiere}</td>
                                        <td className={`px-4 py-2 whitespace-nowrap text-sm font-semibold text-center ${parseFloat(noteItem.note) >= (bulletin.baseNotation / 2) ?
                                            'text-green-600' : 'text-red-600'}`}>
                                            {noteItem.note}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600 text-center">{noteItem.moyenneClasse}</td>
                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600 text-center">{noteItem.coefficient}</td>
                                        <td className="px-4
                                            py-2 whitespace-nowrap text-sm text-gray-800 font-medium text-center">{noteItem.rang}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-6 p-4 bg-indigo-50 border-l-4 border-indigo-500 rounded-lg">
                        <h5 className="text-md font-semibold text-indigo-800 mb-1">Appréciation du Conseil de Classe</h5>
                        <p className="text-sm text-indigo-700 italic">{bulletin.appreciations ||
                            "Aucune appréciation enregistrée pour ce bulletin."}</p>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">* La colonne Moyenne Classe affiche la moyenne générale de la classe, car les moyennes par matière ne sont pas stockées individuellement pour chaque élève dans votre structure de bulletin.</p>
                </div>
            </div>
        );
    };

    // ----------------------------------------------------------------------------------
    // --- BLOC DE RENDU SÉQUENTIEL ---
    // ----------------------------------------------------------------------------------

    if (selectedBulletin) {
        return <BulletinDetail
            bulletin={selectedBulletin}
            schoolInfo={schoolInfo}
            onBackToList={handleBackToList}
            onExportToPDF={exportBulletinToPDF}
        />;
    }

    // Étape 1: Afficher un loader pendant la résolution initiale de l'identité
    if (isResolvingIdentity) {
        return (
            <div className="flex justify-center items-center h-48">
                <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mr-3" />
                <p className="text-gray-600">
                    {isParentRole ? "Recherche des enfants liés..." : "Identification de votre compte élève..."}
                </p>
            </div>
        );
    }

    // Étape 2: Déterminer si l'identité est résolue APRÈS la résolution (isResolvingIdentity est false ici)
    const isEntityResolved = isParentRole ? children.length > 0 : !!studentEleveId;

    // Si la résolution a échoué (pas d'enfant pour le parent, pas d'ID d'élève résolu)
    if (!isEntityResolved) {
        if (isParentRole && children.length === 0) {
            return (
                <div className="p-10 text-center bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-xl shadow-lg max-w-4xl mx-auto">
                    <Users className="w-8 h-8 mx-auto mb-3" />
                    <p className="text-lg font-semibold">Aucun Enfant Lié</p>
                    <p>Veuillez contacter l'administration pour lier vos enfants à votre compte afin de consulter leurs bulletins.</p>
                </div>
            );
        }
        return (
            <div className="p-10 text-center bg-red-50 border border-red-200 text-red-700 rounded-xl shadow-lg max-w-4xl mx-auto">
                <Info className="w-8 h-8 mx-auto mb-3" />
                <p className="text-lg font-semibold">Erreur d'Identification</p>
                <p>Impossible d'identifier l'élève. Votre compte n'est peut-être pas lié à un dossier élève actif ou à une inscription valide pour l'année scolaire en cours.</p>
            </div>
        );
    }

    // Étape 3: Afficher le loader pendant la récupération des enregistrements (Si l'identité est résolue et qu'on attend les données)
    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-48">
                <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mr-3" />
                <p className="text-gray-600">
                    Chargement des bulletins de {entityToDisplay?.name || "l'élève sélectionné"}...
                </p>
            </div>
        );
    }


    // Vue Liste des Bulletins (Étape 4: Affichage final)
    return (
        <div className="space-y-6 p-4">
            <div className='flex flex-col md:flex-row justify-between items-start md:items-center border-b pb-2 mb-4'>
                <h3 className="text-2xl font-semibold text-gray-800 flex items-center mb-4 md:mb-0">
                    <BookOpen className="w-6 h-6 mr-2 text-indigo-600" />
                    {isParentRole ? "Bulletins de mes Enfants" : "Mes Bulletins de Notes"}
                </h3>

                {/* SÉLECTEUR D'ENFANT (UNIQUEMENT POUR PARENT) */}
                {isParentRole && children.length > 0 && (
                     <div className="relative w-full md:w-64 z-10">
                        <button
                            onClick={() => children.length > 1 && setIsDropdownOpen(!isDropdownOpen)}
                            className={`w-full text-left bg-indigo-50 border ${children.length > 1 ? 'border-indigo-300 hover:border-indigo-500' : 'border-indigo-200 cursor-default'} rounded-xl p-3 flex items-center justify-between shadow-sm transition duration-150`}
                            title="Sélectionner un enfant"
                        >
                            <div className='flex items-center'>
                                <Filter className="w-5 h-5 mr-2 text-indigo-600" />
                                {entityToDisplay ? (
                                    <span className="font-semibold text-gray-800">
                                        {entityToDisplay.name}
                                    </span>
                                ) : (
                                    <span className="text-gray-500">Choisir un enfant...</span>
                                )}
                            </div>
                            {children.length > 1 && <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${isDropdownOpen ? 'rotate-180' : 'rotate-0'}`} />}
                        </button>

                        {isDropdownOpen && children.length > 1 && (
                            <div className="absolute w-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 max-h-60 overflow-y-auto">
                                {children.map(child => (
                                    <div
                                        key={child.id}
                                        onClick={() => handleChildSelection(child.id)}
                                        className={`p-3 cursor-pointer hover:bg-indigo-100 flex items-center justify-between transition duration-150 ${selectedChildId === child.id ? 'bg-indigo-200 font-bold' : ''}`}
                                    >
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">{child.name}</p>
                                            <p className="text-xs text-gray-500">Classe: {child.className}</p>
                                        </div>
                                        {selectedChildId === child.id && <Check className="w-4 h-4 text-indigo-600" />}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* --- Section de Filtrage --- */}
            <div className="p-4 bg-white rounded-xl shadow-lg border border-gray-200 flex flex-wrap gap-4 items-center mb-6">
                <span className="font-semibold text-gray-800 text-lg flex items-center"><Filter className="w-5 h-5 mr-2 text-indigo-600" /> Filtrer:</span>

                <div className="flex items-center gap-2">
                    <label htmlFor="filter-year" className="text-sm font-medium text-gray-600">Année:</label>
                    <select
                        id="filter-year"
                        value={filterYear}
                        onChange={(e) => setFilterYear(e.target.value)}
                        className="p-2 border border-gray-300 rounded-lg text-sm bg-gray-50 focus:ring-indigo-500 focus:border-indigo-500 appearance-none transition duration-150"
                    >
                        <option value="all">Toutes</option>
                        {availableYears.map(year => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>
                </div>

                <div className="flex items-center gap-2">
                    <label htmlFor="filter-period" className="text-sm font-medium text-gray-600">Période:</label>
                    <select
                        id="filter-period"
                        value={filterPeriod}
                        onChange={(e) => setFilterPeriod(e.target.value)}
                        className="p-2 border border-gray-300 rounded-lg text-sm bg-gray-50 focus:ring-indigo-500 focus:border-indigo-500 appearance-none transition duration-150"
                    >
                        <option value="all">Toutes</option>
                        {availablePeriods.map(period => (
                            <option key={period} value={period}>{period}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* --- Liste des Bulletins --- */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg border border-gray-200">
                <div className="px-4 py-5 sm:px-6 bg-indigo-50 border-b">
                    <h3 className="text-lg leading-6 font-medium text-indigo-800">
                        Liste des Bulletins disponibles pour <span className='font-bold'>{entityToDisplay?.name || 'cet élève'}</span>
                    </h3>
                </div>

                {bulletins.length === 0 ? (
                    <div className="p-8 text-center text-gray-500 flex items-center justify-center">
                         <Info className="w-5 h-5 mr-2" />
                        Aucun bulletin de notes enregistré pour le moment avec les filtres appliqués.
                    </div>
                ) : (
                    <ul className="divide-y divide-gray-200">
                        {bulletins.map((bulletin) => (
                            <li key={bulletin.id} className="p-4 hover:bg-gray-50 transition duration-150
                                flex justify-between items-center">
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 flex items-center">
                                        <FileText size={18} className="mr-2 text-indigo-500" />
                                        Bulletin de {bulletin.periode}
                                    </p>
                                    <p className="mt-1 text-sm text-gray-500 truncate">
                                        Année: <span className="font-semibold text-gray-700">{bulletin.anneeScolaire}</span>
                                        {' - '}
                                        Classe: <span className="font-semibold text-gray-700">{bulletin.classeNom ||
                                        'N/A'}</span>
                                    </p>
                                </div>

                                <div className="text-right">
                                    <p className="text-xs text-gray-600">Moy.
                                        Générale</p>
                                    <p className="text-lg font-bold text-indigo-600">
                                        {bulletin.moyenneGenerale !== undefined ?
                                        parseFloat(bulletin.moyenneGenerale).toFixed(2) : 'N/A'}
                                        /{bulletin.baseNotation}
                                    </p>
                                </div>
                                <button
                                    onClick={() => handleViewBulletin(bulletin)}
                                    className="ml-4 bg-indigo-100 text-indigo-700 hover:bg-indigo-200 py-2 px-3 rounded-lg text-sm font-medium transition duration-150"
                                >
                                    Voir Détail
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            <p className='text-xs text-gray-400 p-2 border-t mt-4'>
                **Note technique:** {isParentRole ?
                    `Vous consultez les bulletins de l'enfant sélectionné (Eleve ID: ${selectedChildId || 'N/A'}). La requête Firestore utilise le champ 'eleveDetails.eleveId' avec cet ID.` :
                    `Vous consultez vos propres bulletins. La requête Firestore utilise le champ 'eleveDetails.authId' avec votre Auth UID (${studentEleveId || 'N/A'}).`
                }
            </p>
        </div>
    );
};

export default MyBulletins;
